<template>
    <div>
        <ts-page-title
            :title="$t('motorWithdraw.pageTitle')"
            :breadcrumb="[
                { text: $t('home'), href: '/' },
                { text: $t('motorWithdraw.pageTitle'), active: true }
            ]"
        />
        <ts-panel>
            <ts-panel-wrapper
                class="tw-flex tw-items-center tw-justify-between"
            >
                <Button type="info" ghost @click="addNew">{{
                    $t("addNew")
                }}</Button>
                <div class="tw-justify-end tw-space-x-2">
                    <Input
                        search
                        v-model="search"
                        :placeholder="$t('motorWithdraw.search')"
                        :style="width"
                        @on-focus="() => (width = 'width: 500px')"
                        @on-blur="() => (width = 'width: 300px')"
                    />
                    <ts-button
                        v-tooltip="$t('filter')"
                        @click.prevent="visible = true"
                    >
                        <i class="fa fa-filter"></i>
                    </ts-button>
                </div>
            </ts-panel-wrapper>
            <Table
                :columns="columns"
                :data="resources"
                size="small"
                :loading="loading"
                max-height="700"
                stripe
            >
                <template slot-scope="{ row }" slot="outstanding_before">
                    {{ row.currency_sign
                    }}{{ formatNumber(row.outstanding_before) }}
                </template>
                <template slot-scope="{ row }" slot="outstanding_after">
                    {{ row.currency_sign
                    }}{{ formatNumber(row.outstanding_after) }}
                </template>
                <template slot-scope="{ row }" slot="deducted_value">
                    {{ row.currency_sign
                    }}{{ formatNumber(row.deducted_value) }}
                </template>
                <template slot-scope="{ row }" slot="customer_name">
                    {{ row.customer_name }}
                </template>
                <template slot-scope="{ row }" slot="branch">
                    {{ row.branch }}
                </template>
                <template slot-scope="{ row }" slot="action">
                    <a
                        href="#"
                        class="text-primary ml-4"
                        @click.prevent="onEdit(row)"
                        v-tooltip="$t('edit')"
                    >
                        <Icon type="ios-create" size="20" />
                    </a>

                    <Poptip
                        :title="$t('are_you_sure_to_delete')"
                        confirm
                        :transfer="true"
                        @on-ok="onDelete(row)"
                        v-if="!row._deleting"
                    >
                        <a class="ml-2 text-danger" v-tooltip="$t('delete')">
                            <Icon type="ios-trash" size="20" />
                        </a>
                    </Poptip>

                    <a v-else class="ml-2 text-danger">
                        <i class="fas fa-circle-notch fa-spin"></i>
                    </a>
                </template>
            </Table>
            <div class="tw-flex tw-items-center tw-justify-end tw-p-4">
                <ts-i-pagination
                    v-model="pagination"
                    @navigate="fetchData"
                ></ts-i-pagination>
            </div>
            <Drawer
                :title="$t('filter')"
                :closable="false"
                :value="visible"
                @on-close="() => (visible = false)"
                width="300px"
            >
                <div class="mb-3">
                    <label class="form-col-label control-label">{{
                        $t("branchName")
                    }}</label>
                    <ts-branch-filter @filter="value => (branch_id = value)" />
                </div>
            </Drawer>
            <Modal
                v-model="showForm"
                draggable
                sticky
                scrollable
                :mask="false"
                :footer-hide="true"
                :z-index="1022"
                :title="$t('motorWithdraw.pageTitle')"
                width="800"
            >
                <from-action
                    ref="form_action"
                    @cancel="clearEdit"
                    @fetchData="fetchData"
                />
            </Modal>
        </ts-panel>
    </div>
</template>

<script>
import { debounce } from "lodash";
import { mapGetters, mapState } from "vuex";
import FromAction from "./form";

export default {
    name: "motorWithdrawIndex",
    components: { FromAction },
    data() {
        return {
            loading: false,
            showForm: false,
            visible: false,
            branch_id: [],
            width: "width: 300px"
        };
    },
    computed: {
        ...mapState("creditOperation/motorWithdraw", [
            "resources",
            "pagination"
        ]),
        ...mapGetters(["formatCurrencyWithCode", "formatNumber"]),
        search: {
            get() {
                return this.$store.state.creditOperation.motorWithdraw.search;
            },
            set(newValue) {
                this.$store.commit(
                    "creditOperation/motorWithdraw/SET_SEARCH",
                    newValue
                );
                this.$store.commit(
                    "creditOperation/motorWithdraw/RESET_CURRENT_PAGE"
                );
            }
        },
        columns() {
            return [
                {
                    title: this.$t("motorWithdraw.branch"),
                    slot: "branch",
                    sortable: true,
                    minWidth: 150
                },
                {
                    title: this.$t("motorWithdraw.withdrawDate"),
                    key: "withdraw_date",
                    sortable: true,
                    align: "center",
                    minWidth: 150
                },
                {
                    title: this.$t("motorWithdraw.customer"),
                    slot: "customer_name",
                    sortable: true,
                    minWidth: 220
                },
                {
                    title: this.$t("motorWithdraw.motorType"),
                    key: "motor_type",
                    minWidth: 150
                },
                {
                    title: this.$t("motorWithdraw.obefore"),
                    slot: "outstanding_before",
                    align: "right",
                    minWidth: 180
                },
                {
                    title: this.$t("motorWithdraw.oafter"),
                    slot: "outstanding_after",
                    align: "right",
                    minWidth: 180
                },
                {
                    title: this.$t("motorWithdraw.deductedValue"),
                    slot: "deducted_value",
                    align: "right",
                    minWidth: 150
                },
                {
                    title: this.$t("motorWithdraw.reason"),
                    key: "reason",
                    minWidth: 250
                },
                {
                    title: this.$t("motorWithdraw.remainingDebtSolution"),
                    key: "remaining_debt_solution",
                    minWidth: 250
                },
                {
                    title: this.$t("actions"),
                    key: "action",
                    slot: "action",
                    fixed: "right",
                    align: "center",
                    className: "tw-space-x-2",
                    width: 90
                }
            ];
        }
    },
    methods: {
        fetchData(attributes) {
            this.loading = true;
            this.$store
                .dispatch("creditOperation/motorWithdraw/fetch", {
                    ...attributes
                })
                .then(() => {
                    this.loading = false;
                })
                .catch(error => {
                    this.loading = false;
                    this.notice({ type: "error", text: error.message });
                });
        },
        onEdit(record) {
            this.$store.commit(
                "creditOperation/motorWithdraw/SET_EDIT_DATA",
                record
            );
            this.showForm = true;
            this.$refs.form_action.fetchResource();
        },
        clearEdit() {
            this.showForm = false;
            this.$refs.form_action.clearInput();
            this.$store.commit(
                "creditOperation/motorWithdraw/SET_EDIT_DATA",
                {}
            );
        },
        addNew() {
            this.$store.commit(
                "creditOperation/motorWithdraw/SET_EDIT_DATA",
                {}
            );
            this.showForm = true;
            this.$refs.form_action.fetchResource();
            this.$refs.form_action.clearInput();
        },
        notice(not) {
            this.$Notice[not.type]({ title: "MOTOR WITHDRAW", desc: not.text });
        },
        async onDelete(record) {
            record._deleting = true;

            this.$store
                .dispatch("creditOperation/motorWithdraw/destroy", {
                    id: record.withdraw_id
                })
                .then(response => {
                    this.fetchData();

                    this.notice({
                        type: response.message_id == 0 ? "success" : "warning",
                        text: response.message
                    });
                })
                .catch(error => {
                    this.notice({
                        type: "error",
                        text: error.message
                    });
                })
                .finally(() => {
                    record._deleting = false;
                });
        }
    },
    watch: {
        search: debounce(function(search) {
            this.fetchData({ search, branch_id: this.branch_id });
        }, 500),
        branch_id: debounce(function(value) {
            this.fetchData({ branch_id: value });
        }, 500)
    },
    beforeRouteLeave(to, from, next) {
        this.$store.commit("creditOperation/motorWithdraw/RESET_STATE");
        next();
    }
};
</script>
